<template>
    <div class="concept-quiz-cta"
         v-if="showConceptQuizCTA"
         v-dark
    >
        <div class="concept-quiz-cta__header" v-dark>
            <IconTarget class="concept-quiz-cta__icon" />
            <div class="concept-quiz-cta__header-text" v-dark>
                Concept Quiz
            </div>
        </div>
        <div class="concept-quiz-cta__body">
            <div class="concept-quiz-cta__explanation" v-dark>
                <template v-if="conceptQuizTrigger === 'question'">
                    You missed a question on <strong>{{ conceptQuizVariant?.concept }}</strong>.
                </template>
                <template v-else>
                    You viewed the definition for <strong>{{ conceptQuizVariant?.concept }}</strong>.
                </template>
                Would you like to take a 10 question quiz focused on {{ conceptQuizVariant?.concept }}?
            </div>
            <PocketButton
                :is-dark-mode="isDarkMode"
                @click="startConceptQuiz"
                class="concept-quiz-cta__button"
                v-if="breakpoint !== 'black-bear'"
            >
                Start Quiz
            </PocketButton>
            <PocketLink
                :is-dark-mode="isDarkMode"
                @click="startConceptQuiz"
                class="concept-quiz-cta__button"
                v-else
            >
                Start Quiz
            </PocketLink>
        </div>
    </div>
</template>

<script lang="ts">
import { Component, Vue } from 'vue-facing-decorator'
import { quizModule } from '@/store/quiz/module'
import { userModule } from '@/store/user/module'
import UIKit from '@pocketprep/ui-kit'
import { analyticsModule } from '@/store/analytics/module'
import { subscriptionModule } from '@/store/subscription/module'
import { examMetadataModule } from '@/store/examMetadata/module'
import type { Study } from '@pocketprep/types'
import { localISODateString } from '@/utils'
import type { TActiveQuiz } from '@/store/quiz/state'
import { screenModule } from '@/store/screen/module'
import IconTarget from '@/components/ConceptQuizExperiment/IconTarget.vue'

@Component({
    components: {
        PocketButton: UIKit.Button,
        PocketLink: UIKit.Link,
        IconTarget,
    },
})
export default class ConceptQuizCTA extends Vue {
    conceptQuizConfig: {
        concept: string
        examMetadataId: string
        conceptQuizSerials: string[]
        questionSerialTriggers?: string
    }[] = []
    prefetchedConceptQuizQuestions: Study.Class.QuestionJSON[] = []

    get breakpoint () {
        return screenModule.getters.getBreakpoint()
    }

    get isDarkMode () {
        return userModule.state.settings.isDarkMode
    }

    get quizId () {
        return this.$route.params['quizId'] as string
    }

    get currentQuiz () {
        return quizModule.getters.getQuizById(this.quizId)
    }

    get examMetadata () {
        return examMetadataModule.getters.getCurrentExamMetadata()
    }

    get hasSubscription () {
        return !!subscriptionModule.getters.getSubscriptionForExamId(this.examMetadata?.objectId)
    }

    get currentQuizAnswers () {
        if (!this.currentQuiz) {
            return []
        }

        return this.currentQuiz.answers
    }

    get hasTakenConceptQuiz () {
        return userModule.state.user?.webConfig?.hasTakenConceptQuiz
    }

    get triggerKeywordInfo () {
        const triggerKeywordInfoRaw = localStorage.getItem('conceptQuizExperiment_triggerKeywordInfo')
        const triggerKeywordInfo = triggerKeywordInfoRaw && JSON.parse(triggerKeywordInfoRaw) as {
            startedAt: string
            serial: string
            keyword: string
        }
        return triggerKeywordInfo || undefined
    }

    get keywordTriggeredConceptQuiz () {
        const userClickedKeywordInCurrentQuiz = this.triggerKeywordInfo 
            && new Date(this.triggerKeywordInfo.startedAt).toISOString() === this.currentQuiz?.startedAt.iso
        if (!this.triggerKeywordInfo || !userClickedKeywordInCurrentQuiz) {
            return undefined
        }

        return this.triggerKeywordInfo.serial
    }

    get questionTriggeredConceptQuiz () {
        if (!this.conceptQuizConfig) {
            return undefined
        }

        const questionSerialTriggers = this.conceptQuizConfig.flatMap(c => c.questionSerialTriggers)
        const conceptQuizQuestionAnswerTriggerSerial = this.currentQuizAnswers.find(
            a => questionSerialTriggers.includes(a.questionSerial) && !a.isCorrect
        )?.questionSerial

        return conceptQuizQuestionAnswerTriggerSerial
    }

    get conceptQuizVariant () {
        if (this.keywordTriggeredConceptQuiz) {
            return this.conceptQuizConfig.find(c => c.concept === this.triggerKeywordInfo?.keyword)
        } else if (this.questionTriggeredConceptQuiz) {
            return this.conceptQuizConfig.find(c =>
                this.questionTriggeredConceptQuiz
                && c.questionSerialTriggers?.includes(this.questionTriggeredConceptQuiz)
            )
        }

        return undefined
    }

    // did the user click the keyword or incorrectly answer the question that triggers showing them this CTA?
    get conceptQuizTrigger () {
        return this.keywordTriggeredConceptQuiz
            ? 'keyword'
            : this.questionTriggeredConceptQuiz
                ? 'question'
                : false
    }

    get showConceptQuizCTA () {
        // requirements to show this quiz CTA:
        // 1. came directly from quiz page
        // 2. has subscription
        // 3. is in Amplitude experiment
        // 4. has not taken the concept quiz before
        // 5. is in the correct exam
        // 6. is not a mock exam
        // 7. has triggered the concept quiz via clicking a keyword or incorrectly answering a question
        return this.$router.options.history.state.back === '/quiz'
            && this.hasSubscription
            && !this.hasTakenConceptQuiz
            && this.conceptQuizVariant
            && this.conceptQuizVariant.examMetadataId === this.examMetadata?.objectId
            && this.currentQuiz?.mode !== 5
            && !!this.conceptQuizTrigger
    }

    async mounted () {
        const variant = await analyticsModule.actions.amplitudeFetchVariant('concept-quiz')
        this.conceptQuizConfig = variant?.payload || null

        if (!this.conceptQuizVariant || !this.showConceptQuizCTA) {
            return
        }

        // emit whether the concept quiz CTA will be shown so the parent can hide the mobile Retake Quiz button
        // if it is visible
        this.$emit('isConceptQuizCTAVisible', this.showConceptQuizCTA)

        // store this quizId in local storage so we can access its data when we get to the concept quiz survey
        localStorage.setItem('conceptQuizExperiment_quizIdThatTriggeredConceptQuiz', this.quizId)

        const [ conceptQuizQuestions ] = await Promise.all([
            quizModule.actions.fetchQuestionsBySerial({
                examMetadataId: this.conceptQuizVariant.examMetadataId,
                serials: this.conceptQuizVariant.conceptQuizSerials,
            }),
            userModule.actions.fetchUserData(),
            examMetadataModule.actions.fetchExamMetadata(),
        ])
        this.prefetchedConceptQuizQuestions = conceptQuizQuestions

        analyticsModule.actions.amplitudeTrack('Concept Quiz CTA Seen', {
            examGuid: this.examMetadata?.examGuid || '',
            conceptQuizConcept: this.conceptQuizVariant.concept,
            conceptQuizTriggerKeywordClicked: !!this.keywordTriggeredConceptQuiz,
            conceptQuizTriggerQuestionAnswered: !!this.questionTriggeredConceptQuiz,
        })
    }

    startConceptQuiz () {
        const triggerQuestionSerial = this.keywordTriggeredConceptQuiz || this.questionTriggeredConceptQuiz

        if (
            !triggerQuestionSerial
            || !this.prefetchedConceptQuizQuestions?.length 
            || !this.examMetadata 
            || !this.conceptQuizVariant
        ) {
            throw new Error('Unable to start a concept quiz.')
        }

        // filter out the question that triggered the CTA to reduce duplicates
        const conceptQuizQuestions = this.prefetchedConceptQuizQuestions
            .filter(q => q.serial !== triggerQuestionSerial)
            .slice(0, 10)

        const conceptQuiz: TActiveQuiz = {
            questions: conceptQuizQuestions,
            answers: [],
            examGuid: this.examMetadata.examGuid,
            mode: 'concept',
            startedAt: localISODateString(),
            currentQuestionIndex: 0,
            durationSeconds: 0,
            breakDurationSeconds: 0,
            timerType: 'exam',
        }

        quizModule.actions.updateActiveQuiz(conceptQuiz)

        userModule.actions.updateWebConfig({
            hasTakenConceptQuiz: true,
        })

        analyticsModule.actions.amplitudeTrack('Concept Quiz Started', {
            examGuid: this.examMetadata.examGuid || '',
            conceptQuizConcept: this.conceptQuizVariant.concept,
            conceptQuizTriggerKeywordClicked: !!this.keywordTriggeredConceptQuiz,
            conceptQuizTriggerQuestionAnswered: !!this.questionTriggeredConceptQuiz,
        })

        this.$router.push({ name: 'quiz' })
    }
}
</script>

<style lang="scss" scoped>
.concept-quiz-cta {
    background-color: $gray-background;
    margin: 69px 0 -71px;
    padding: 12px 15px 13px 13px;
    width: 100%;
    max-width: 780px;
    border: 1px solid rgba($pewter, 0.3);
    border-radius: 6px;
    box-sizing: border-box;
    color: $brand-black;

    &--dark {
        background: $charcoal;
        border-color: $charcoal;
        color: $white;
    }

    @include breakpoint(brown-bear) {
        max-width: 696px;
        margin: 30px 0 -79px;
    }

    @include breakpoint(black-bear) {
        max-width: 328px;
        background-color: transparent;
        text-align: center;
        padding: 21px 20px 24px 20px;
        border: 1px solid rgba($pewter, 0.85);
        margin: 0 0 -26px;
    }

    &__explanation {
        max-width: 623px;
        font-size: 16px;
        line-height: 22px;
        
        @include breakpoint(brown-bear) {
            max-width: 539px;
        }

        @include breakpoint(black-bear) {
            font-size: 15px;
            line-height: 20px;
            margin-bottom: 16px;
            color: $ash;

            &--dark {
                color: $white;
            }
        }
    }

    &__button {
        width: 109px;
    }

    &__header {
        font-weight: 700;
        font-size: 11px;
        text-transform: uppercase;
        letter-spacing: 1px;
        color: $ash;
        margin-bottom: 8px;
        line-height: 20px;
        display: flex;
        align-items: center;

        &--dark {
            color: $fog;
        }

        @include breakpoint(black-bear) {
            display: block;
            margin-bottom: 5px;
        }
    }

    &__header-text {
        margin-left: 5px;
    }

    &__body {
        display: flex;
        justify-content: space-between;
        align-items: center;

        @include breakpoint(black-bear) {
            display: block;
        }
    }
}
</style>